import { type TargetAndTransition, type Transition } from "motion/react";
import { UI_MOTION } from "../../../config/uiMotion.configuration";
import {
    isUiMotionEnabled,
    resolveUiEaseOut,
    resolveUiSpring,
} from "../../shared/motion/uiMotion.tools";

export type ItineraryPanelViewDirection = "forward" | "back";

interface ItineraryListViewMotionTargets {
    "initial":    TargetAndTransition | false;
    "animate":    TargetAndTransition;
    "exit":       TargetAndTransition;
    "transition": Transition;
}

/**
 * Resolves horizontal push/pop motion targets for itinerary panel view switches.
 *
 * @param direction Navigation direction between list and details.
 * @param reducedMotion Whether the user prefers reduced motion.
 * @returns Motion target triple for the active itinerary panel view.
 */
export const getItineraryListViewMotionTargets = (
    direction: ItineraryPanelViewDirection,
    reducedMotion: boolean,
): ItineraryListViewMotionTargets => {
    if ( !isUiMotionEnabled( reducedMotion ) ) {
        return {
            "animate":    { "opacity": 1 },
            "exit":       { "opacity": 1 },
            "initial":    { "opacity": 1 },
            "transition": resolveUiSpring( reducedMotion, "gentle" ),
        };
    }

    const enterX = direction === "forward" ? 12 : -12;
    const exitX = direction === "forward" ? -12 : 12;

    return {
        "animate": {
            "opacity": 1,
            "x":       0,
        },
        "exit": {
            "opacity":    0,
            "x":          exitX,
            "transition": resolveUiEaseOut( reducedMotion, UI_MOTION.duration.fastMs ),
        },
        "initial": {
            "opacity": 0,
            "x":       enterX,
        },
        "transition": resolveUiSpring( reducedMotion, "gentle" ),
    };
};
